import axios from 'axios';
import type { CancelTokenSource } from 'axios';

import type { CryptoOperation, CryptoWalletVo } from 'models/crypto';

import type { ServerError } from '../../models';
import type {
    CryptoAccessTokenRequest,
    CryptoWithdrawalResponse,
    CryptoWithdrawalResquest,
    DeleteCryptoWalletRequest,
    GetCryptoAccessTokenResponse,
    GetCryptoAccountDetailsResponse,
    GetCryptoWalletResponse,
    GetMeshLinkTokenRequest,
    GetMeshLinkTokenResponse,
    GetMeshSupportedNetworksResponse,
    GetMeshWithdrawalAddressRequest,
    GetMeshWithdrawalAddressesResponse,
    InitiateMeshWithdrawalRequest,
    MeshIntegrationToken,
    MeshNetworkDetails,
    MeshWithdrawalAddress,
} from '../../models/reqres';
import { buildQueryString } from '../../utils/query';
import { exchangeApi, handleErrorCallback, noop } from '../core';

export function getCryptoAccountDetails(
    userId: string,
    onSuc: (status: string, code: string) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetCryptoAccountDetailsResponse>(`/users/${userId}/crypto/account`, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data.status, response.data.participant_code);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function createCryptoAccount(
    userId: string,
    citizenship: string,
    onSuc: (status: string, code: string) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
    sardineSessionKey?: string,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .put<GetCryptoAccountDetailsResponse>(
            `/users/${userId}/crypto/account`,
            {
                citizenship,
                sardine_session_key: sardineSessionKey,
            },
            {
                cancelToken: cancelRef.token,
            },
        )
        .then((response) => {
            onSuc(response.data.status, response.data.participant_code);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getCryptoAccessToken(
    userId: string,
    onSuc: (token: string) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
    sardineSessionKey?: string,
    trackingProps?: CryptoAccessTokenRequest,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();
    const payload: CryptoAccessTokenRequest = {
        sardine_session_key: sardineSessionKey,
        ...trackingProps,
    };

    exchangeApi
        .post<GetCryptoAccessTokenResponse>(`/users/${userId}/crypto/access_token`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data.token);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getCryptoWallets(
    userId: string,
    onSuc: (wallets: CryptoWalletVo[]) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetCryptoWalletResponse>(`/users/${userId}/crypto/wallets`, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data.crypto_wallets);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function deleteCryptoWallet(
    payload: DeleteCryptoWalletRequest,
    onSuc: () => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .delete(`users/${payload.user_id}/crypto/wallets/${payload.wallet_id}`, {
            cancelToken: cancelRef.token,
        })
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function createCryptoWithdrawal(
    userId: string,
    payload: CryptoWithdrawalResquest,
    onSuc: (token: string) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post<CryptoWithdrawalResponse>(`/users/${userId}/crypto/withdrawal`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data.token);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getMeshLinkToken(
    userId: string,
    operation: CryptoOperation,
    onSuc: (token: string, integrationTokens: MeshIntegrationToken[]) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
    trackingProps?: GetMeshLinkTokenRequest,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    const path =
        operation === 'deposit' ? 'deposit_link_token' : operation === 'withdraw' ? 'withdrawal_link_token' : '';
    exchangeApi
        .get<GetMeshLinkTokenResponse>(`users/${userId}/mesh/${path}${buildQueryString(trackingProps)}`, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data.link_token, response.data.integration_tokens ?? []);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getMeshWithdrawalAddresses(
    userId: string,
    payload: GetMeshWithdrawalAddressRequest,
    onSuc: (addresses: MeshWithdrawalAddress[]) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetMeshWithdrawalAddressesResponse>(
            `users/${userId}/mesh/withdrawal_addresses${buildQueryString(payload)}`,
            {
                cancelToken: cancelRef.token,
            },
        )
        .then((response) => {
            onSuc(response.data.wallet_addresses);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function initiateMeshWithdrawal(
    userId: string,
    payload: InitiateMeshWithdrawalRequest,
    onSuc: () => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post(`users/${userId}/mesh/initiate_mesh_withdrawal`, payload, {
            cancelToken: cancelRef.token,
        })
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getMeshSupportedNetworks(
    onSuc: (networks: MeshNetworkDetails[]) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetMeshSupportedNetworksResponse>('payments/mesh/supported_networks', {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data.network_details);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}
